import { useState } from 'react';
import { signatureService } from '../../services/api';
import { useToast } from '../../context/ToastContext';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  Button,
  Label,
} from '../ui';
import { Loader2, PenLine, AlertCircle, CheckCircle } from 'lucide-react';

const signatureTypeLabels = {
  acknowledgment: 'Presa visione',
  approval: 'Approvazione',
  consent: 'Consenso',
};

/**
 * Dialog per firmare un documento
 */
function SignDocumentDialog({ document, open, onClose, onSigned }) {
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const [notes, setNotes] = useState('');
  const [confirmed, setConfirmed] = useState(false);

  const handleSign = async () => {
    if (!confirmed) {
      toast.error('Errore', 'Devi confermare di aver letto il documento');
      return;
    }

    setIsLoading(true);
    try {
      await signatureService.sign(document._id, { notes: notes || undefined });

      toast.success('Documento firmato', 'La tua firma è stata registrata con successo');
      onSigned?.();
      handleClose();
    } catch (error) {
      toast.error('Errore', error.response?.data?.message || 'Impossibile firmare il documento');
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setNotes('');
    setConfirmed(false);
    onClose();
  };

  if (!document) return null;

  const signatureType = document.signatureType || 'acknowledgment';

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base sm:text-lg">
            <PenLine className="h-5 w-5 flex-shrink-0" />
            Firma documento
          </DialogTitle>
          <DialogDescription className="text-sm">
            Stai per firmare: <strong>{document.title}</strong>
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 sm:space-y-4 py-2 sm:py-4">
          {/* Tipo di firma */}
          <div className="flex items-start gap-3 p-3 bg-muted rounded-lg">
            <AlertCircle className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
            <div>
              <p className="font-medium text-sm sm:text-base">
                Tipo: {signatureTypeLabels[signatureType]}
              </p>
              {document.signatureInstructions && (
                <p className="text-xs sm:text-sm text-muted-foreground mt-1">
                  {document.signatureInstructions}
                </p>
              )}
            </div>
          </div>

          {/* Scadenza */}
          {document.signatureDeadline && (
            <p className="text-xs sm:text-sm text-muted-foreground">
              Scadenza: {new Date(document.signatureDeadline).toLocaleDateString('it-IT', {
                day: 'numeric',
                month: 'long',
                year: 'numeric',
              })}
            </p>
          )}

          {/* Note */}
          <div className="space-y-2">
            <Label htmlFor="notes" className="text-sm">Note (opzionale)</Label>
            <textarea
              id="notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Eventuali note..."
              className="w-full min-h-[60px] sm:min-h-[80px] px-3 py-2.5 rounded-md border border-input bg-background text-base sm:text-sm focus:outline-none focus:ring-2 focus:ring-ring resize-none"
              maxLength={500}
              style={{
                fontSize: '16px',
              }}
            />
          </div>

          {/* Checkbox conferma */}
          <div className="flex items-start gap-3 p-3 border border-border rounded-lg">
            <input
              type="checkbox"
              id="confirm"
              checked={confirmed}
              onChange={(e) => setConfirmed(e.target.checked)}
              className="mt-0.5 h-5 w-5 rounded border-input flex-shrink-0"
            />
            <label htmlFor="confirm" className="text-xs sm:text-sm cursor-pointer leading-relaxed">
              Confermo di aver letto e compreso il contenuto del documento.
              Sono consapevole che questa firma ha valore legale.
            </label>
          </div>

          {/* Info validità legale */}
          <div className="flex items-start gap-2 text-xs text-muted-foreground">
            <CheckCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
            <p>
              La tua firma verrà registrata con data, ora, indirizzo IP e hash del documento per garantirne la validità legale.
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose} className="min-h-[44px] touch-manipulation">
            Annulla
          </Button>
          <Button onClick={handleSign} disabled={!confirmed || isLoading} className="min-h-[44px] touch-manipulation">
            {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            <PenLine className="mr-2 h-4 w-4" />
            Firma documento
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default SignDocumentDialog;
